import DuoMascot from '@/components/v2/DuoMascot'
import DuoSpeechBubble from '@/components/v2/DuoSpeechBubble'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

/**
 * Duolingo's recall drill: a character says a word or phrase in the learner's
 * language and the answers are the target-language forms taught in this lesson.
 *
 * Generic over the id type so the same drill serves single words at beginner
 * level and whole phrases from elementary up.
 */
export default function MeaningExercise<Id extends string>({
  correctId,
  locked,
  onSelect,
  options,
  prompt,
  selected,
  titleKey,
  vocabulary,
}: {
  correctId: Id
  locked: boolean
  onSelect: (option: Id) => void
  options: Id[]
  /** The spoken word or phrase, in the learner's own language. */
  prompt: string
  selected?: Id
  titleKey: string
  vocabulary: Record<Id, string>
}) {
  const { t } = useTranslation()

  return (
    <div className="flex grow flex-col">
      <h1 className="duo-heading">{t(titleKey)}</h1>

      <div className="mt-6 flex items-start gap-3">
        <DuoMascot className="h-14 w-14 shrink-0" />

        <div className="pt-1">
          <DuoSpeechBubble tail="left">
            <p className="text-duo-ink text-lg font-bold">{prompt}</p>
          </DuoSpeechBubble>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {options.map((option) => {
          const isSelected = selected === option
          const isCorrect = option === correctId

          return (
            <button
              className={twMerge(
                'border-duo-line text-duo-ink rounded-2xl border-2 border-b-4 bg-white px-4 py-4 text-center text-base font-bold transition-colors duration-150',
                isSelected &&
                  'border-duo-primary bg-duo-primary-soft text-duo-primary',
                locked &&
                  isCorrect &&
                  'border-duo-green bg-duo-green-soft text-duo-green-text',
                locked &&
                  isSelected &&
                  !isCorrect &&
                  'border-duo-red bg-duo-red-soft text-duo-red-text',
                !locked && 'active:mt-0.5 active:border-b-2',
              )}
              disabled={locked}
              key={option}
              onClick={() => onSelect(option)}
              type="button"
            >
              {vocabulary[option]}
            </button>
          )
        })}
      </div>
    </div>
  )
}
